import DateFormat from '@/Enums/Format/DateFormat.enum';
import PixelStatus from '@/Enums/Pixels/PixelStatus.enum';
import ReportProperty from '@/Enums/Report/BeeswaxReportProperties.enum';
import type { IAdvertiser } from '@/Interfaces/Advertiser.interface';
import type { IDbFirstPartySegment } from '@/Interfaces/Beeswax/FirstPartySegment.interface';
import type {
  IControlOption,
  IPixelAdvertiserControlOption,
  ISegmentControlOption,
} from '@/Interfaces/ControlOption.interface';
import type { IApiTransformedEvent, IEventMetrics, IParsedEvent } from '@/Interfaces/Pixels/Event.interface';
import type { IPixelReportRecord } from '@/Interfaces/Pixels/Report.interface';
import { DEFAULT_PIXELS_FIRED_BY_DAY } from '@/constants/pixels';
import moment from 'moment';
import { enumerateDaysBetweenDates, getDaysInSeconds } from '../dateFormatter';

export const processApiPixelsResponse = (pixels: IApiTransformedEvent[]): IParsedEvent[] => {
  if (!pixels?.length) return [];

  return pixels.map(event => ({
    id: event.id,
    eventId: event.eventId,
    name: event.name,
    active: event.active,
    advertiser: { value: event.advertiserId, label: event.advertiserName },
    segment: event.segmentId
      ? { value: event.segmentId, label: event.segmentName || '', advertiserId: event.advertiserId, active: true }
      : null,
    status: PixelStatus.NotFiring,
    dateCreated: event.dateCreated,
    firedToday: 0,
    firedLast7Days: 0,
    pixelsFiredByDay: [...DEFAULT_PIXELS_FIRED_BY_DAY],
    action: event.id,
    clickWindow: `${getDaysInSeconds(event.clickWindow ?? 0)}`,
    viewWindow: `${getDaysInSeconds(event.viewWindow ?? 0)}`,
  }));
};

export const processDbSegmentsResponse = (segments: IDbFirstPartySegment[] = []): ISegmentControlOption[] => {
  if (!segments.length) return [];

  return segments.map(segment => ({
    value: segment.id,
    label: segment.name,
    advertiserId: segment.advertiserId,
    active: true,
  }));
};

const getEventFiresMetrics = (
  reportingData: IPixelReportRecord[] = [],
): Map<IPixelReportRecord[ReportProperty.EVENT_ID], IEventMetrics> => {
  const mappedFireMetrics = new Map<IPixelReportRecord[ReportProperty.EVENT_ID], IEventMetrics>();
  const rangeEnd = new Date();
  const rangeStart = new Date();
  rangeStart.setDate(rangeStart.getDate() - 6);

  const rangeEndString = moment(rangeEnd).format(DateFormat.Iso8601);
  const rangeStartString = moment(rangeStart).format(DateFormat.Iso8601);

  reportingData
    .sort((a, b) => moment(b.Day, DateFormat.Iso8601).diff(moment(a.Day, DateFormat.Iso8601)))
    .map(event => {
      const {
        [ReportProperty.EVENT_ID]: id,
        [ReportProperty.DAY]: day,
        [ReportProperty.EVENT_TAG_FIRE_COUNT]: fireCount,
      } = event;
      let currentMapEntry = mappedFireMetrics.get(id);

      if (!currentMapEntry) {
        currentMapEntry = {
          pixelsFiredByDay: Object.fromEntries(
            enumerateDaysBetweenDates(rangeStartString, rangeEndString).map(date => [date, 0]),
          ),
          firedToday: 0,
          firedLast7Days: 0,
        };
      }

      currentMapEntry.pixelsFiredByDay[day] = fireCount;
      currentMapEntry.firedLast7Days = currentMapEntry.firedLast7Days + fireCount;
      if (!currentMapEntry.firedToday && moment().format(DateFormat.Iso8601) === day) {
        currentMapEntry.firedToday = fireCount;
      }

      mappedFireMetrics.set(id, currentMapEntry);
    });

  return mappedFireMetrics;
};

export const mergeEvents = (
  events: IParsedEvent[] = [],
  reportingEvents: IPixelReportRecord[] = [],
): IParsedEvent[] => {
  if (!events.length || !reportingEvents.length) {
    return events ?? [];
  }
  const mappedMetrics = getEventFiresMetrics(reportingEvents);

  return events.map(event => {
    const metricsRecord = mappedMetrics.get(event.eventId);
    if (!metricsRecord) return event;

    let status = event.status;

    const pixelsFiredByDay = Object.entries(metricsRecord.pixelsFiredByDay).map(([date, value]) => {
      if (value) {
        status = PixelStatus.Firing;
      }

      return {
        date: moment(date).format(DateFormat.ShortMonthDay),
        value,
      };
    });

    return {
      ...event,
      ...metricsRecord,
      pixelsFiredByDay,
      status,
    };
  });
};

export const mergeSegmentsAndAdvertisers = (
  segments: ISegmentControlOption[] = [],
  advertisers: IControlOption[] = [],
): IPixelAdvertiserControlOption[] => {
  const advertiserSegmentsMap = new Map<IAdvertiser['id'], ISegmentControlOption[]>();

  segments.forEach(segment => {
    const { advertiserId } = segment;
    if (!advertiserId) {
      return;
    }
    const mapRecord = advertiserSegmentsMap.get(advertiserId) || [];

    advertiserSegmentsMap.set(advertiserId, [...mapRecord, segment]);
  });

  return advertisers.map(advertiser => ({
    ...advertiser,
    segments: advertiserSegmentsMap.get(advertiser.value) || [],
  }));
};

export const createNewSegmentControlOption = (
  advertiserId: IAdvertiser['id'],
  segmentName: string,
  id: IDbFirstPartySegment['id'],
): ISegmentControlOption => ({
  label: segmentName,
  advertiserId,
  value: id,
  active: true,
});

export const trimPixelTag = (tag: string = ''): string => {
  if (!tag) return '';

  return tag.replace(/<!--.+-->/g, '').trim();
};
